import { useId, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { IconChevronDown, IconInfoCircle } from "@tabler/icons-react";
import { useDelayedTooltip } from "@/hooks/useDelayedTooltip";

type CollapsibleSectionProps = {
  /** Header label shown next to the chevron. */
  title: string;
  /**
   * Quiet "(optional)" tag rendered right of the title — same whisper the
   * ConditionsDrawer trigger row uses, so a section the user may ignore says so
   * before they open it.
   */
  optionalLabel?: string;
  /**
   * "What does this option do?" — an ⓘ next to the title plus a delayed
   * {@link PInfo} bubble on hover OR keyboard focus of the header. House rule:
   * the explanation lives in the hint, not as a grey helper line. Also exposed
   * to assistive tech via `aria-describedby`, since a hover-only bubble reaches
   * nobody who can't hover.
   */
  infoMessage?: string;
  /** Open on first render. Seed from "does this already have values set" so
   *  existing configuration is visible on open instead of hidden. */
  defaultOpen?: boolean;
  /**
   * Hold the section open regardless of the user's toggle — for a blocker that
   * lives INSIDE the body (an incomplete row blocking Save). Collapsing it away
   * would hide the very thing the user has to fix; the toggle reports the
   * enforced state and the click is a deliberate no-op.
   */
  forceOpen?: boolean;
  /**
   * Quiet status rendered on the right of the header while COLLAPSED (e.g. a
   * "configured" chip or a short value preview). Hidden once expanded — the
   * fields themselves are the content then. Omit for a plain disclosure.
   */
  summary?: React.ReactNode;
  /**
   * `card` — full-width bordered header row; the section IS the surface.
   * `quiet` — a small tertiary text button ("Advanced options ⌄") that carries
   * no box of its own. Use inside a form that already has boxed rows (toggles,
   * inputs): a second bordered row there reads like another field instead of
   * the whisper an optional escape hatch should be.
   */
  variant?: "card" | "quiet";
  /**
   * Fires once a USER-initiated open has finished expanding, with the
   * section's root element — e.g. to scroll a section that opens below the
   * fold into view. After the expand, not on the click: while the panel is
   * still 0px high there is nothing below the header to reveal. Never for an
   * open nobody asked for (`defaultOpen` / `forceOpen`), never on a fold.
   */
  onOpened?: (section: HTMLDivElement) => void;
  /** Body layout override. Defaults to the roomy "advanced options" stack. */
  bodyClassName?: string;
  testId?: string;
  children: React.ReactNode;
};

/**
 * Neutral, animated "Advanced options" disclosure — the shared home for the
 * inline collapsible pattern that had been copy-pasted per feature folder
 * (device editors, website settings) and hand-rolled inline elsewhere. Prefer
 * this over a click-to-open Modal when a section holds a handful of OPTIONAL
 * fields: a popup is overkill for that, and inline keeps the values in place.
 *
 * Children mount only while open (collapsing unmounts them), so hosts must own
 * the edited state — every current caller does. That keeps effects inside the
 * body (live counts, fetches) from running behind a closed chevron.
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  optionalLabel,
  infoMessage,
  defaultOpen = false,
  forceOpen = false,
  summary,
  variant = "card",
  onOpened,
  bodyClassName = "pt-5 flex flex-col gap-6",
  testId,
  children,
}) => {
  const quiet = variant === "quiet";
  const [selfOpen, setSelfOpen] = useState(defaultOpen);
  const open = forceOpen || selfOpen;
  const panelId = useId();
  const hintId = useId();
  const reduceMotion = useReducedMotion();
  // A section seeded open (it already has values) must not play its open
  // animation on mount — only a real user toggle animates.
  const hasToggledRef = useRef(false);
  // Set by a click that OPENS, consumed when that expand completes (`onOpened`).
  const userOpeningRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelInitial = hasToggledRef.current
    ? { height: 0, opacity: 0 }
    : false;
  const duration = reduceMotion ? 0 : 0.2;

  // Hover AND focus live on the header button itself: a bubble wired to the
  // icon <span> would never see focus (focusin bubbles up from the button, it
  // does not travel down into a descendant), leaving keyboard users with no
  // way to read the explanation. The measuring ref stays on the icon so the
  // bubble still points at the ⓘ.
  const { ref: hintRef, tooltip, ...hintHandlers } =
    useDelayedTooltip<HTMLSpanElement>(infoMessage);

  return (
    <div ref={rootRef} className="flex flex-col" data-testid={testId}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-describedby={infoMessage ? hintId : undefined}
        onClick={() => {
          if (forceOpen) return;
          hasToggledRef.current = true;
          userOpeningRef.current = !selfOpen;
          setSelfOpen((v) => !v);
        }}
        {...hintHandlers}
        className={
          quiet
            ? "flex items-center gap-1.5 w-max max-w-full py-1 px-1 -ml-1 rounded-md focus-ring transition-colors group cursor-pointer"
            : "flex items-center justify-between gap-2 w-full py-2.5 px-3 border outline-quaternary rounded-lg hover:surface-secondary focus-ring transition-colors group"
        }
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {infoMessage && (
            <span ref={hintRef} className="inline-flex shrink-0 font-secondary">
              <IconInfoCircle size={quiet ? 14 : 16} aria-hidden />
            </span>
          )}
          <p
            className={
              quiet
                ? "body-3-medium font-secondary group-hover:font-primary min-w-0 truncate transition-colors"
                : "body-2-medium font-primary min-w-0 truncate"
            }
          >
            {title}
          </p>
          {optionalLabel && (
            <span className="body-3-regular font-quaternary shrink-0">
              {optionalLabel}
            </span>
          )}
        </div>
        <div
          className={
            quiet
              ? "flex items-center gap-1.5 min-w-0"
              : "flex items-center gap-2 min-w-0"
          }
        >
          {!open && (
            <span
              className={
                quiet ? "min-w-0 truncate body-3-regular font-tertiary" : "min-w-0 truncate"
              }
            >
              {summary}
            </span>
          )}
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration }}
            className="shrink-0"
          >
            <IconChevronDown
              size={quiet ? 14 : 18}
              className="font-secondary group-hover:font-primary transition-colors"
            />
          </motion.div>
        </div>
      </button>
      {/* Same words as the bubble, reachable without hovering. */}
      {infoMessage && (
        <span id={hintId} className="sr-only">
          {infoMessage}
        </span>
      )}
      {tooltip}

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            role="region"
            aria-label={title}
            initial={panelInitial}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration }}
            onAnimationComplete={() => {
              if (!userOpeningRef.current) return;
              userOpeningRef.current = false;
              // Not right here: framer reports an INSTANT transition (reduced
              // motion, duration 0) complete before it writes the panel's
              // final `height: auto` — measured, the callback saw the panel
              // at 0px and the host's scrollIntoView revealed the header
              // alone. Two frames on the panel is laid out at full height
              // whichever way it opened.
              requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                  if (rootRef.current) onOpened?.(rootRef.current);
                }),
              );
            }}
            className="overflow-hidden"
          >
            <div className={bodyClassName}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
